// A board game on the table in 3D (chess, checkers): the pieces (sliding to
// their new squares), the last move, the check, the picked piece and its legal
// destinations, seat labels with clocks and a status line over the table.
//
// In VR the seated player moves on the real board: poke or pinch a piece, then
// its destination (bare hands, or controller rays). Desktop / phone players
// use the overlay instead (a canvas click there would also walk the avatar).
// Table-local, floor origin.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { Billboard, Text } from '@react-three/drei';
import { useXR } from '@react-three/xr';
import { noRaycast } from '../../VR/vrUi';
import { VRBtn, vrColors as colors } from '../kit/vrKit';
import { BOARD_CHAIRS, BOARD_Y, SQ, sqXZ } from './boardLayout';
import { fmtClock, useBoardPicker, type BoardGameUI, type PieceSpec } from './boardGame';
import type { Table3DProps } from '../types';

const HL_Y = BOARD_Y + 0.0012;

// ---- stable piece identities (so pieces slide instead of popping) ----------
interface Tracked extends PieceSpec { id: number }

/** Match the new position's pieces to the old ones: same square first, then the
 *  nearest moved piece of the same side and kind (castling rook, en passant),
 *  then any of the same side (promotion, crowning). */
function track(prev: Tracked[], next: PieceSpec[], nextId: { n: number }): Tracked[] {
  const free = new Set(prev.map((_, i) => i));
  const out: (Tracked | null)[] = next.map(() => null);
  next.forEach((p, i) => {
    const j = prev.findIndex((q, k) => free.has(k) && q.sq === p.sq && q.side === p.side && q.kind === p.kind);
    if (j >= 0) { free.delete(j); out[i] = { ...p, id: prev[j].id }; }
  });
  const dist = (a: number, b: number) => Math.hypot((a & 7) - (b & 7), (a >> 3) - (b >> 3));
  for (const sameKind of [true, false]) {
    next.forEach((p, i) => {
      if (out[i]) return;
      let best = -1, bd = Infinity;
      prev.forEach((q, k) => {
        if (!free.has(k) || q.side !== p.side || (sameKind && q.kind !== p.kind)) return;
        const d = dist(q.sq, p.sq);
        if (d < bd) { bd = d; best = k; }
      });
      if (best >= 0) { free.delete(best); out[i] = { ...p, id: prev[best].id }; }
    });
  }
  return next.map((p, i) => out[i] ?? { ...p, id: nextId.n++ });
}

const SLIDE_S = 0.35;
const _v = new THREE.Vector3();

function AnimatedPiece({ p, ui, onTap, lifted }: { p: Tracked; ui: BoardGameUI; onTap: ((sq: number) => void) | null; lifted: boolean }) {
  const ref = useRef<THREE.Group>(null);
  const anim = useRef<{ from: THREE.Vector3; t: number } | null>(null);
  const [x, z] = sqXZ(p.sq);
  const first = useRef(true);
  useEffect(() => {
    const g = ref.current;
    if (!g) return;
    if (first.current) { first.current = false; g.position.set(x, BOARD_Y, z); return; }
    anim.current = { from: g.position.clone(), t: 0 };
  }, [x, z]);
  useFrame((_, dt) => {
    const g = ref.current;
    if (!g) return;
    const a = anim.current;
    const lift = lifted ? 0.012 : 0;
    if (!a) { g.position.set(x, BOARD_Y + lift, z); return; }
    a.t = Math.min(1, a.t + dt / SLIDE_S);
    const e = a.t < 0.5 ? 2 * a.t * a.t : 1 - (-2 * a.t + 2) ** 2 / 2;
    _v.set(x, BOARD_Y, z);
    g.position.lerpVectors(a.from, _v, e);
    g.position.y = BOARD_Y + Math.sin(a.t * Math.PI) * 0.03 + lift;
    if (a.t >= 1) anim.current = null;
  });
  const Piece = ui.Piece;
  return (
    <group ref={ref} name={`board-piece-${p.sq}`}
      onClick={onTap ? (e) => { e.stopPropagation(); onTap(p.sq); } : undefined}>
      <Piece kind={p.kind} side={p.side} />
      {/* The models don't raycast; this invisible column takes the poke / ray while you can move. */}
      {onTap && (
        <mesh position={[0, 0.04, 0]}>
          <cylinderGeometry args={[SQ * 0.4, SQ * 0.4, 0.08, 8]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} colorWrite={false} />
        </mesh>
      )}
    </group>
  );
}

/** Pieces with stable ids across positions. */
function Pieces({ ui, pieces, onTap, picked }: { ui: BoardGameUI; pieces: PieceSpec[]; onTap: ((sq: number) => void) | null; picked: number | null }) {
  const prev = useRef<Tracked[]>([]);
  const ids = useRef({ n: 1 });
  const tracked = useMemo(() => {
    const t = track(prev.current, pieces, ids.current);
    prev.current = t;
    return t;
  }, [pieces]);
  return <>{tracked.map((p) => <AnimatedPiece key={p.id} p={p} ui={ui} onTap={onTap} lifted={picked === p.sq} />)}</>;
}

function SquareMark({ sq, color, opacity = 0.55, size = 0.94, ring = false }: { sq: number; color: string; opacity?: number; size?: number; ring?: boolean }) {
  const [x, z] = sqXZ(sq);
  return (
    <mesh position={[x, HL_Y, z]} rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast as any} renderOrder={2}>
      {ring ? <ringGeometry args={[SQ * 0.26, SQ * 0.36, 20]} /> : <planeGeometry args={[SQ * size, SQ * size]} />}
      <meshBasicMaterial color={color} transparent opacity={opacity} depthWrite={false} toneMapped={false} />
    </mesh>
  );
}

/** Invisible poke / pinch targets over every square (VR, your move). */
function HitSquares({ onTap }: { onTap: (sq: number) => void }) {
  return <>{Array.from({ length: 64 }, (_, i) => {
    const [x, z] = sqXZ(i);
    return (
      <mesh key={i} name={`board-sq-${i}`} position={[x, BOARD_Y + 0.012, z]} onClick={(e) => { e.stopPropagation(); onTap(i); }}>
        <boxGeometry args={[SQ * 0.98, 0.024, SQ * 0.98]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} colorWrite={false} />
      </mesh>
    );
  })}</>;
}

/** Re-render every `ms` while `on`. */
function useTicker(on: boolean, ms = 500) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!on) return;
    const t = window.setInterval(() => force((n) => n + 1), ms);
    return () => window.clearInterval(t);
  }, [on, ms]);
}

export function BoardTable3D({ ui, pid, def, table, seat }: Table3DProps & { ui: BoardGameUI }) {
  const st = table?.state;
  const g = st?.g ?? null;
  const presenting = useXR((s) => !!s.session);
  const playing = st?.phase === 'playing' && !!g;
  const rules = playing && presenting && seat >= 0 ? ui.rules(g, seat) : null;
  const { path, targets, promo, tap, choosePromo } = useBoardPicker(pid, rules);
  const pieces = useMemo(() => (g ? ui.pieces(g) : []), [g, ui]);
  const clocks = g && ui.clocks ? ui.clocks(g, Date.now()) : null;
  useTicker(!!clocks && playing);
  const last = g ? ui.last(g) : [];
  const check = g ? ui.check(g) : null;
  const res = g && st?.phase !== 'lobby' ? ui.result(g) : null;
  const turn = g && playing ? ui.turn(g) : null;
  const names = [0, 1].map((i) => st?.seats[i]?.name ?? def.seatNames?.[i] ?? `Seat ${i + 1}`);

  const status = !st ? 'Loading…'
    : st.phase === 'lobby' ? `${st.seats.filter(Boolean).length}/2 seated — sit down to play`
    : res ? res.text
    : turn !== null ? `${def.seatNames?.[turn] ?? names[turn]} to move${ui.drawOffer(g) !== null ? ' · draw offered' : ''}` : '';

  const promoChair = promo && seat >= 0 ? BOARD_CHAIRS[seat] : null;
  const promoSq = promo ? promo[promo.length - 1] : -1;

  return (
    <>
      {/* Last move, check, selection, legal targets */}
      {last.map((s) => <SquareMark key={`l${s}`} sq={s} color="#e0b04a" opacity={0.4} />)}
      {check !== null && <SquareMark sq={check} color="#e23b3b" opacity={0.6} />}
      {path.map((s, i) => <SquareMark key={`p${s}-${i}`} sq={s} color={colors.teal} opacity={0.55} />)}
      {targets.map((s) => <SquareMark key={`t${s}`} sq={s} color={colors.teal} opacity={0.9} ring />)}
      <Pieces ui={ui} pieces={pieces} onTap={rules ? tap : null} picked={path.length ? path[path.length - 1] : null} />
      {rules && !promo && <HitSquares onTap={tap} />}

      {/* Promotion choice, floating over the square, facing the player */}
      {promoChair && ui.promoChoices && (
        <group position={[sqXZ(promoSq)[0], BOARD_Y + 0.16, sqXZ(promoSq)[1]]} rotation={[0, promoChair.yaw + Math.PI, 0]}>
          {ui.promoChoices.map((c, i) => (
            <VRBtn key={c.v} name={`vr-board-promo-${c.v}`} label={c.label} x={(i - (ui.promoChoices!.length - 1) / 2) * 0.1} y={0} w={0.09} h={0.06}
              fontSize={0.016} onPress={() => choosePromo(c.v)} />
          ))}
        </group>
      )}

      {/* Seat labels (behind each chair) */}
      {BOARD_CHAIRS.map((ch, i) => {
        const s = st?.seats[i];
        const side = def.seatNames?.[i] ?? '';
        const label = s ? `${s.name}${s.kind === 'bot' ? ' 🤖' : ''}${i === seat ? ' (you)' : ''}` : `${side} — open`;
        const line2 = [side && s ? side : '', clocks ? fmtClock(clocks[i]) : ''].filter(Boolean).join(' · ');
        return (
          <Billboard key={i} position={[ch.x * 1.25, 1.45, ch.z * 1.25]}>
            <Text fontSize={0.075} color={turn === i ? colors.teal : '#ffffff'} outlineWidth={0.006} outlineColor="#000" raycast={noRaycast} anchorY="bottom">{label}</Text>
            {line2 && <Text position={[0, -0.02, 0]} fontSize={0.06} color="#d7e3ee" outlineWidth={0.005} outlineColor="#000" raycast={noRaycast} anchorY="top">{line2}</Text>}
          </Billboard>
        );
      })}
      <Billboard position={[0, 1.62, 0]}>
        <Text fontSize={0.1} color="#ffffff" outlineWidth={0.007} outlineColor="#000" raycast={noRaycast} anchorY="bottom">{def.label}</Text>
        <Text position={[0, -0.02, 0]} fontSize={0.065} color={res ? colors.teal : '#d7e3ee'} outlineWidth={0.005} outlineColor="#000" raycast={noRaycast} anchorY="top" maxWidth={2.2} textAlign="center">{status}</Text>
      </Billboard>
    </>
  );
}

export default BoardTable3D;
